/**
 * The English copy, and the key list every other language answers.
 *
 * Flat and dotted rather than nested: a key is grepped for far more often than
 * it is browsed, and `users.dialog.delete.title` finds its one call site
 * immediately. Keys ending `_one` are English singulars — see `translate`.
 */
export const en: Record<string, string> = {
  // ── Shared ────────────────────────────────────────────────────────────────
  'common.any': 'Any',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.on': 'On',
  'common.off': 'Off',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.save': 'Save',
  'common.saveChanges': 'Save changes',
  'common.saveTheseChanges': 'Save these changes?',
  'common.delete': 'Delete',
  'common.restore': 'Restore',
  'common.remove': 'Remove',
  'common.close': 'Close',
  'common.loading': 'Loading',
  'common.loadingEllipsis': 'Loading…',
  'common.dismiss': 'Dismiss',
  'common.searchPlaceholder': 'Search…',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.none': 'None',
  'common.never': 'Never',
  'common.always': 'Always',
  'common.noLimit': 'No limit',
  'common.noCap': 'No cap',
  'common.name': 'Name',
  'common.description': 'Description',
  'common.enabled': 'Enabled',
  'common.disabled': 'Disabled',
  'common.priority': 'Priority',
  'common.template': 'Template',
  'common.selectTemplate': 'Select a template…',
  'common.title': 'Title',
  'common.body': 'Body',
  'common.language': 'Language',
  'common.userId': 'User id',
  'common.deviceId': 'Device id',
  'common.pasteDeviceId': 'Paste a device id',
  'common.somethingWentWrong': 'Something went wrong',
  'common.nothingToShow': 'Nothing to show',
  'common.done': 'Done — recorded in the activity log',
  'common.reason': 'Reason',
  'common.reasonHint': 'Recorded in the activity log against your name',
  'common.reasonPlaceholder': 'Why are you doing this?',
  'common.results': '{count} results',
  'common.results_one': '{count} result',
  'common.pageOf': 'Page {page} of {totalPages}',
  'common.requestFailed': 'Request failed ({status})',
  'common.users': '{count} users',
  'common.users_one': '{count} user',
  'common.creditSuffix': 'cr',
  'common.available': 'Available:',

  // ── Activity drill-down ───────────────────────────────────────────────────
  'activity.metric.top_up': 'Credits topped up',
  'activity.metric.premium': 'Went Premium',
  'activity.metric.revenue': 'Payments taken',
  'activity.metric.new_user': 'New users',
  'activity.metric.woke_up': 'Woke up',
  'activity.metric.missed': 'Missed the challenge',
  'activity.metric.forfeited': 'Stakes forfeited',
  'activity.metric.payout': 'Rewards paid out',
  'activity.metric.spent': 'Credits spent',
  'activity.metric.adjusted': 'Credits adjusted by hand',

  'activity.about.top_up':
    'Credits added to a wallet — bought, granted or earned from an ad.',
  'activity.about.premium':
    'Premium periods bought — by card or with daylights. Operator grants are ' +
    'in the activity log instead.',
  'activity.about.revenue': 'Cash the bank took, net of anything refunded.',
  'activity.about.new_user': 'Accounts registered.',
  'activity.about.woke_up': 'Challenges beaten before the time ran out.',
  'activity.about.missed': 'Challenges not beaten in time.',
  'activity.about.forfeited': 'Stakes lost to a missed challenge.',
  'activity.about.payout': 'Shares of a settled pool credited to a wallet.',
  'activity.about.spent': 'Credits spent on something in the app.',
  'activity.about.adjusted': 'Credits moved by an operator by hand.',

  'activity.scope.today': 'Since midnight.',
  'activity.scope.window': 'Over the last {days} days.',

  'activity.total.users': 'People',
  'activity.total.credits': 'Credits',
  'activity.total.money': 'Amount',
  'activity.count.top_up': 'Top-ups',
  'activity.count.premium': 'Periods',
  'activity.count.revenue': 'Payments',
  'activity.count.new_user': 'Registrations',
  'activity.count.woke_up': 'Wins',
  'activity.count.missed': 'Misses',
  'activity.count.forfeited': 'Forfeits',
  'activity.count.payout': 'Payouts',
  'activity.count.spent': 'Purchases',
  'activity.count.adjusted': 'Adjustments',

  'activity.times': '{count} ×',
  'activity.premium': 'Premium',
  'activity.deleted': 'Deleted',
  'activity.empty': 'Nobody yet',
  'activity.emptyDescription':
    'Nothing of this kind has happened in this window.',

  // ── Shell ─────────────────────────────────────────────────────────────────
  'nav.brand': 'Alarm Console',
  'nav.dashboard': 'Dashboard',
  'nav.users': 'Users',
  'nav.alarms': 'Alarms',
  'nav.rewards': 'Rewards',
  'nav.notifications': 'Notifications',
  'nav.conversions': 'Mode switches',
  'nav.activity': 'Activity',
  'nav.backTo.user': 'Back to the user',
  'nav.backTo.alarm': 'Back to the alarm',
  'nav.backTo.event': 'Back to the event',
  'nav.open': 'Open navigation',
  'nav.close': 'Close navigation',
  'nav.signedInAs': 'Signed in as',
  'nav.light': 'Light',
  'nav.dark': 'Dark',
  'nav.signOut': 'Sign out',
  'nav.language': 'Language',

  // ── Login ─────────────────────────────────────────────────────────────────
  'login.subtitle': 'Sign in to manage users, alarms and rewards',
  'login.username': 'Username',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.note': 'Credentials are configured in the server environment.',

  // ── Dashboard ─────────────────────────────────────────────────────────────
  'dashboard.title': 'Dashboard',
  'dashboard.description': 'Today so far, and the last {days} days',
  'dashboard.policy':
    'Stake {cost} per ring · rewards {window} · set {notice} ahead',
  'dashboard.attention.claims': 'Reward claims awaiting review',
  'dashboard.attention.claimsCaption':
    'Stakes held until someone rules on them — open Alarms → Review',
  'dashboard.attention.jobs': 'Notifications past due',
  'dashboard.attention.jobsCaption':
    'The queue is behind — check Notifications → Queue',

  'dashboard.morning.title': 'This morning',
  'dashboard.morning.description': 'Since midnight, in your timezone',
  'dashboard.morning.settled': '{count} settled in {days} days',
  'dashboard.morning.wokeUp': 'Woke up',
  'dashboard.morning.wokeUpCaption': '{percent} of settled rings',
  'dashboard.morning.noRings': 'No rings settled yet',
  'dashboard.morning.missed': 'Missed',
  'dashboard.morning.missedCaption': '{percent} of settled',
  'dashboard.morning.rings': 'Alarms rang',
  'dashboard.morning.ringsCaption': '{count} rang out unsolved',
  'dashboard.morning.solve': 'Average solve',
  'dashboard.morning.solveCaption': 'Time to beat the challenge',
  'dashboard.morning.wakeRate': 'Wake rate over {days} days',
  'dashboard.morning.breakdown':
    '{earned} won · {missed} missed · {withheld} withheld pending review',

  'dashboard.today.title': 'Today in business',
  'dashboard.today.description':
    'Money and membership since midnight, in your timezone',
  'dashboard.today.hint': 'Tap a figure to see who is behind it',
  'dashboard.today.toppedUp': 'Credits topped up',
  'dashboard.today.toppedUpCaption': '{users} users · {count} top-ups',
  'dashboard.today.toppedUpCaption_one': '{users} users · {count} top-up',
  'dashboard.today.premium': 'Went Premium',
  'dashboard.today.premiumCaption': '{count} on Premium now',
  'dashboard.today.revenue': 'Taken today',
  'dashboard.today.revenueCaption': '{count} payments · {users} users',
  'dashboard.today.revenueCaption_one': '{count} payment · {users} users',
  'dashboard.today.newUsers': 'New users',
  'dashboard.today.newUsersCaption': '{count} this week',

  'dashboard.economy.title': 'The economy',
  'dashboard.economy.description':
    'Holdings now, and what moved over {days} days',
  'dashboard.economy.held': 'Credits held by users',
  'dashboard.economy.heldCaption': 'Everything they own',
  'dashboard.economy.staked': 'Staked on live alarms',
  'dashboard.economy.stakedCaption': '{percent} of all credits',
  'dashboard.economy.won': 'Won from other users',
  'dashboard.economy.wonCaption': 'Lifetime payouts',
  'dashboard.economy.commission': 'Commission taken',
  'dashboard.economy.commissionCaption': '{count} settlements in the window',
  'dashboard.economy.commissionCaption_one': '{count} settlement in the window',
  'dashboard.economy.toppedUp': 'Topped up',
  'dashboard.economy.forfeited': 'Forfeited',
  'dashboard.economy.paidOut': 'Paid out',
  'dashboard.economy.adjusted': 'Adjusted by hand',
  'dashboard.economy.shared': '{amount} shared between {count} users',
  'dashboard.economy.shared_one': '{amount} shared between {count} user',
  'dashboard.economy.carryOver': '{amount} carried into the next cycle',

  'dashboard.population.title': 'Population',
  'dashboard.population.description':
    'New accounts, and how many of them actually rang an alarm',
  'dashboard.population.premium': 'Premium users',
  'dashboard.population.premiumCaption': '{percent} of users',
  'dashboard.population.newToday': 'New today',
  'dashboard.population.newTodayCaption': '{count} this week',
  'dashboard.population.total': 'Total users',
  'dashboard.population.totalCaption': '{count} seen today',
  'dashboard.population.liveAlarms': 'Live alarms',
  'dashboard.population.liveAlarmsCaption': '{total} total · {reward} staking',
  'dashboard.population.pushable': 'Reachable by push',
  'dashboard.population.pushableCaption': '{percent} of users',

  'dashboard.charts.wake': 'Getting up',
  'dashboard.charts.wakeDescription':
    'Challenges won and missed, per day, over {days} days',
  'dashboard.charts.credits': 'Credits in motion',
  'dashboard.charts.creditsDescription':
    'What missed mornings took, and what settlements gave back',
  'dashboard.series.wokeUp': 'Woke up',
  'dashboard.series.missed': 'Missed',
  'dashboard.series.toppedUp': 'Topped up',
  'dashboard.series.forfeited': 'Forfeited',
  'dashboard.series.paidOut': 'Paid out',
  'dashboard.series.newUsers': 'New users',
  'dashboard.series.activeUsers': 'Users who rang',

  'dashboard.breakdown.platforms': 'Platforms',
  'dashboard.breakdown.platformsDescription':
    'Devices, and how many of them subscribe',
  'dashboard.breakdown.subscribed': 'subscribed',
  'dashboard.breakdown.challenges': 'Challenges',
  'dashboard.breakdown.challengesDescription':
    'Alarms by challenge kind, and the mornings they won',
  'dashboard.breakdown.won': 'won',
  'dashboard.breakdown.countries': 'Countries',
  'dashboard.breakdown.countriesDescription': 'Where the users are',
  'dashboard.breakdown.risk': 'Claim risk',
  'dashboard.breakdown.riskDescription':
    'How the rules judged reward claims over {days} days',
  'dashboard.breakdown.riskEmpty':
    'No reward claims have been ruled on in this window.',
  'dashboard.breakdown.empty': 'Nothing recorded yet.',

  // ── Charts ────────────────────────────────────────────────────────────────
  'chart.showChart': 'Show chart',
  'chart.showData': 'Show data',
  'chart.date': 'Date',

  // ── Users ─────────────────────────────────────────────────────────────────
  'users.title': 'Users',
  'users.description': 'Every account, with what it holds and how it performs',
  'users.sortBy': 'Sort by',
  'users.sort.registered': 'Newest first',
  'users.sort.lastSeen': 'Last seen',
  'users.sort.balance': 'Balance',
  'users.sort.earned': 'Lifetime winnings',
  'users.sort.alarms': 'Alarm count',
  'users.sort.wakeRate': 'Wake rate',
  'users.column.account': 'Account',
  'users.column.platform': 'Platform',
  'users.column.balance': 'Balance',
  'users.column.won': 'Won',
  'users.column.alarms': 'Alarms',
  'users.column.wakeRate': 'Wake rate',
  'users.column.lastSeen': 'Last seen',
  'users.stakedTitle': '{amount} staked',
  'users.stakedInline': '({amount} staked)',
  'users.tabsLabel': 'Account sections',
  'users.tab.overview': 'Overview',
  'users.tab.alarms': 'Alarms',
  'users.tab.wallet': 'Wallet',
  'users.tab.payouts': 'Winnings',
  'users.deleted': 'Deleted {when}. Its history is untouched.',
  'users.silencedBy': 'Silenced by {actor}',
  'users.section.wallet': 'Wallet',
  'users.row.balance': 'Balance',
  'users.row.staked': 'Staked on live alarms',
  'users.row.spendable': 'Spendable',
  'users.row.wonFromSettlements': 'Won from settlements',
  'users.wonOver': '{amount} over {count} payouts',
  'users.wonOver_one': '{amount} over {count} payout',
  'users.section.behaviour': 'Behaviour',
  'users.row.alarms': 'Alarms',
  'users.alarmsLive': '{enabled} live of {total}',
  'users.row.challengesWon': 'Challenges won',
  'users.row.challengesMissed': 'Challenges missed',
  'users.row.wakeRate': 'Wake rate',
  'users.nothingSettled': 'Nothing settled yet',
  'users.section.device': 'Device',
  'users.row.userId': 'User id',
  'users.row.owner': 'Signed in as',
  'users.ownerNone': 'Not signed in',
  'users.ownerNameMissing': 'Name not shared',
  'users.row.deviceMoved': 'Moved to this phone',
  'users.row.platform': 'Platform',
  'users.row.appVersion': 'App version',
  'users.row.languageCountry': 'Language / country',
  'users.row.attestation': 'Attestation',
  'users.row.pushToken': 'Push token',
  'users.pushPresent': 'Present',
  'users.pushNone': 'None — cannot be reached',
  'users.row.lastIp': 'Last IP',
  'users.row.registered': 'Registered',
  'users.row.lastSeen': 'Last seen',
  'users.section.subscription': 'Subscription',
  'users.row.status': 'Status',
  'users.subActive': 'Active',
  'users.row.expires': 'Expires',
  'users.grantSubscription': 'Grant subscription',
  'users.cancelSubscription': 'Cancel it',
  'users.tab.freeTrial': 'Free trial',
  'users.noTrialEvents': 'No free-trial history for this account.',
  'users.trialBy': 'by {actor}',
  'users.trialWindow': '{from} → {to}',
  'users.trialType.granted': 'Granted',
  'users.trialType.denied': 'Refused',
  'users.trialType.ended': 'Ended by the user',
  'users.trialType.expired': 'Expired',
  'users.trialType.converted': 'Became a paid plan',
  'users.trialType.extended': 'Extended',
  'users.trialType.revoked': 'Revoked',
  'users.trialReason.disabled': 'Free trials were switched off.',
  'users.trialReason.device_already_trialed':
    'This device has already had a free trial — most likely a reinstall.',
  'users.trialReason.ip_limit_reached':
    'Too many trials from this address inside the policy window.',
  'users.trialReason.already_on_trial': 'A trial was already running.',
  'users.trialReason.already_premium': 'The account already had Premium.',
  'users.trialReason.account_inactive': 'The account is deactivated.',
  'users.section.freeTrial': 'Free trial',
  'users.trialActive': 'Running',
  'users.trialEnded': 'Ended',
  'users.row.trialStarted': 'Started',
  'users.row.trialEnds': 'Ends',
  'users.grantFreeTrial': 'Grant free trial',
  'users.extendFreeTrial': 'Extend it',
  'users.revokeFreeTrial': 'End it now',
  'users.accessMode.subscription': 'Subscribed',
  'users.accessMode.free_trial': 'Free trial',
  'users.accessMode.reward': 'Reward mode',
  'users.row.accessMode': 'Mode',
  'users.filter.isFreeTrial': 'On a free trial',
  'users.dialog.grantTrial.title': 'Grant a free trial',
  'users.dialog.grantTrial.description':
    'Premium for nothing, from now. Recorded in the same trial log a registration writes to, so the funnel stays countable and one trial per device keeps working.',
  'users.dialog.revokeTrial.title': 'End this free trial?',
  'users.dialog.revokeTrial.description':
    'Premium stops immediately. The dates are kept, so the history still reads correctly.',
  'users.section.account': 'Account',
  'users.deactivate': 'Deactivate',
  'users.reactivate': 'Reactivate',
  'users.deleteAccount': 'Delete account',
  'users.days': 'Days',
  'users.daysHint': 'Counted from now',

  'users.dialog.activate.title': 'Reactivate this account?',
  'users.dialog.activate.description':
    'The user will be able to use the app again.',
  'users.dialog.deactivate.title': 'Deactivate this account?',
  'users.dialog.deactivate.description':
    'The account stops working but keeps its history and its credits. The usual response to abuse.',
  'users.dialog.delete.title': 'Delete this account?',
  'users.dialog.delete.description':
    'A soft delete — the account and its stake stay on the books and every event it produced is kept.',
  'users.dialog.restore.title': 'Restore this account?',
  'users.dialog.restore.description':
    'The account and everything it held come back.',
  'users.dialog.subscribe.title': 'Grant a subscription',
  'users.dialog.subscribe.description':
    'Opens a subscription window from now. Infinite alarms need one of these.',
  'users.dialog.unsubscribe.title': 'Cancel this subscription?',
  'users.dialog.unsubscribe.description':
    'Closes the window. The historical dates are kept, because support gets asked about them.',

  'users.op.credit': 'Add credits',
  'users.op.creditHint': 'A top-up, a goodwill credit, a correction upwards.',
  'users.op.debit': 'Take credits',
  'users.op.debitHint': 'Refuses to overdraw the spendable balance.',
  'users.op.release': 'Free a stake',
  'users.op.releaseHint': 'The usual fix for an alarm wedged holding credits.',
  'users.op.block': 'Stake credits',
  'users.op.blockHint': 'Rarely what you want — alarms stake their own.',
  'users.op.forfeit': 'Forfeit a stake',
  'users.op.forfeitHint':
    'Takes the credits for good, as a missed challenge does.',
  'users.moveByHand': 'Move credits by hand',
  'users.operation': 'Operation',
  'users.amount': 'Amount',
  'users.amountHint': 'Whole credits',
  'users.ledger': 'Ledger',
  'users.ledgerLine': '{reason} · {when} · balance {balance}',
  'users.nothingMoved': 'Nothing moved — the funds were not there',
  'users.moved': 'Moved {amount}',
  'users.moveTheCredits': 'Move the credits',
  'users.adjust': 'Adjust',
  'users.viewAlarms': 'Alarms ({count})',
  'users.noAlarms': 'This account has no live alarms.',
  'users.alarmLine': '{schedule} · {won} won / {missed} missed',
  'users.openInAlarmList': 'Open in the alarm list',
  'users.noPayouts':
    'This user has never been allocated a share of a settlement.',
  'users.payoutLine':
    'Finished #{rank} · weight {weight} · {percent}% of the pot · {when}',

  // ── Alarms ────────────────────────────────────────────────────────────────
  'alarms.title': 'Alarms',
  'alarms.pageTabsLabel': 'Alarm sections',
  'alarms.tab.all': 'All alarms',
  'alarms.scopedToUser': "Showing one account's alarms",
  'alarms.showEveryone': 'Show everyone',
  'alarms.description': 'What everyone has set, and what it is staking',
  'alarms.filter.label': 'Label contains',
  'alarms.filter.type': 'Type',
  'alarms.filter.enabled': 'Enabled',
  'alarms.filter.show': 'Show',
  'alarms.filter.live': 'Live alarms',
  'alarms.filter.holding': 'Only ones staking credits',
  'alarms.filter.deleted': 'Include deleted',
  'alarms.filter.sort': 'Sort by',
  'alarms.sort.default': 'Newest alarm',
  'alarms.sort.defaultScoped': 'Time of day',
  'alarms.sort.lastEvent': 'Last event',
  'alarms.type.reward': 'Reward',
  'alarms.type.infinite': 'Infinite',
  'alarms.column.alarm': 'Alarm',
  'alarms.column.schedule': 'Schedule',
  'alarms.column.type': 'Type',
  'alarms.column.challenge': 'Challenge',
  'alarms.column.staked': 'Staked',
  'alarms.column.record': 'Won / missed',
  'alarms.column.lastRang': 'Last rang',
  'alarms.column.lastEvent': 'Last event',
  'alarms.noLabel': '(no label)',
  'alarms.deletedBadge': 'Deleted',
  'alarms.holding':
    "Holding {amount} of this user's balance. Switching it off or deleting it gives that straight back.",
  'alarms.section.schedule': 'Schedule',
  'alarms.row.ringsAt': 'Rings at',
  'alarms.row.repeats': 'Repeats',
  'alarms.row.perWeek': 'Rings per week',
  'alarms.row.tzOffset': 'Timezone offset',
  'alarms.notReported': 'Not reported',
  'alarms.minutesShort': '{count} min',
  'alarms.row.enabled': 'Enabled',
  'alarms.section.challenge': 'Challenge',
  'alarms.row.kind': 'Kind',
  'alarms.row.difficulty': 'Difficulty',
  'alarms.row.rounds': 'Rounds',
  'alarms.row.timeLimit': 'Time limit',
  'alarms.section.record': 'Record',
  'alarms.row.rang': 'Rang',
  'alarms.rangTimes': '{count} times',
  'alarms.row.dismissed': 'Dismissed',
  'alarms.row.challengesWon': 'Challenges won',
  'alarms.row.challengesMissed': 'Challenges missed',
  'alarms.row.wakeRate': 'Wake rate',
  'alarms.row.lifetimeCost': 'Lifetime cost',
  'alarms.row.lastRang': 'Last rang',
  'alarms.row.lastDismissed': 'Last dismissed',
  'alarms.row.lastEvent': 'Last event',
  'alarms.section.identity': 'Identity',
  'alarms.row.alarmId': 'Alarm id',
  'alarms.row.user': 'User',
  'alarms.row.device': 'Device',
  'alarms.row.clientId': 'Device-side id',
  'alarms.row.created': 'Created',
  'alarms.changeTime': 'Change the time',
  'alarms.hour': 'Hour',
  'alarms.minute': 'Minute',
  'alarms.label': 'Label',
  'alarms.editTimeAndLabel': 'Edit time and label',
  'alarms.switchOff': 'Switch off',
  'alarms.switchOn': 'Switch on',
  'alarms.dialog.delete.title': 'Delete this alarm?',
  'alarms.dialog.restore.title': 'Restore this alarm?',
  'alarms.dialog.save.title': 'Save these changes?',
  'alarms.dialog.off.title': 'Switch this alarm off?',
  'alarms.dialog.on.title': 'Switch this alarm on?',
  'alarms.dialog.delete.description':
    'A soft delete. Whatever it is staking goes back to the user immediately.',
  'alarms.dialog.other.description':
    'The stake is brought in line with what the alarm now needs.',
  'alarms.drawerFallback': 'Alarm',
  'alarms.tabsLabel': 'Alarm sections',
  'alarms.tab.detail': 'Details',
  'alarms.tab.events': 'Events',
  'alarms.viewUser': 'View the user',

  // ── Events ────────────────────────────────────────────────────────────────
  'events.tab.review': 'Review queue',
  'events.viewAlarm': 'View the alarm',
  'events.viewUser': 'View the user',
  'events.viewEvent': 'Event details',
  'events.noneForAlarm': 'Nothing logged yet',
  'events.noneForAlarmDescription':
    'This alarm has reported nothing of this kind.',
  'events.section.belongsTo': 'Belongs to',
  'events.section.what': 'What happened',
  'events.section.metadata': 'Everything reported',
  'events.noAlarm': 'This event is not attached to an alarm.',
  'events.row.occurred': 'Happened',
  'events.row.received': 'Reported',
  'events.row.eventId': 'Event id',
  'events.filter.kind': 'Kind',
  'events.filter.risk': 'How the rules judged it',
  'events.type.all': 'Everything',
  'events.type.rang': 'Alarm rang',
  'events.type.dismissed': 'Dismissed',
  'events.type.reward_earned': 'Challenge won',
  'events.type.reward_missed': 'Challenge missed',
  'events.type.reward_withheld': 'Claim withheld',
  'events.type.ring_timed_out': 'Rang out unsolved',
  'events.type.created': 'Alarm created',
  'events.type.deleted': 'Alarm deleted',
  'events.type.rejected': 'Save refused',
  'events.type.integrity_failed': 'Attestation failed',
  'events.risk.trusted': 'Trusted',
  'events.risk.suspicious': 'Suspicious',
  'events.risk.rejected': 'Rejected',
  'events.column.what': 'What happened',
  'events.column.credits': 'Credits',
  'events.detail.solvedIn': 'solved in {duration}',
  'events.detail.ruledBy': 'ruled on by {actor}',
  'events.detail.alarm': 'alarm #{id}',
  'events.review.title': 'Claims awaiting a ruling',
  'events.review.description':
    'Their stakes stay blocked until someone decides. Approving grants the reward; rejecting forfeits the stake.',
  'events.review.emptyTitle': 'Nothing to review',
  'events.review.emptyDescription':
    'Every reward claim so far has been settled by the rules on its own.',
  'events.review.approve': 'Approve',
  'events.review.reject': 'Reject',
  'events.review.ruled': 'Ruled on — the decision is in the event log',
  'events.review.approveTitle': 'Grant this reward?',
  'events.review.rejectTitle': 'Reject this claim and forfeit the stake?',
  'events.review.approveDescription':
    'The reward is granted and the stake stays where it is, ready for the next ring.',
  'events.review.rejectDescription':
    'The stake is taken, exactly as a missed challenge would take it.',
  'events.review.approveConfirm': 'Grant it',
  'events.review.rejectConfirm': 'Forfeit the stake',
  'events.review.view.pending': 'Pending',
  'events.review.view.history': 'History',
  'events.review.historyTitle': 'Ruled claims',
  'events.review.historyDescription':
    'Every ruling made here, newest first, with who made it and why.',
  'events.review.historyEmptyTitle': 'No rulings yet',
  'events.review.historyEmptyDescription':
    'Claims approved or rejected from the queue will show up here.',
  'events.review.approved': 'Approved',
  'events.review.rejected': 'Rejected',
  'events.review.forfeited': '{amount} credits forfeited',
  'events.review.viewClaim': 'Original claim',

  // ── Offline rewards ──────────────────────────────────────────────────────
  'offline.tab': 'Offline rewards',
  'offline.stat.completions': 'Won offline',
  'offline.stat.window': 'Last {days} days',
  'offline.stat.users': 'Accounts',
  'offline.stat.allowance': '{allowance} paid per {days} days, then held',
  'offline.stat.repeatUsers': 'Over the allowance',
  'offline.stat.repeatCaption': 'Repeat pattern — consider deactivating',
  'offline.stat.outcomes': 'Paid',
  'offline.stat.outcomesCaption': '{withheld} held for review · {lost} lost',
  'offline.users.title': 'Accounts winning without internet',
  'offline.users.description':
    'With no internet the scan is swapped for a harder math challenge. The first {allowance} wins in {days} days are paid and flagged; later ones wait in the review queue. Open an account to deactivate it.',
  'offline.users.emptyTitle': 'No offline rewards',
  'offline.users.emptyDescription':
    'Nobody has won a reward without internet in the last {days} days.',
  'offline.column.account': 'Account',
  'offline.column.completions': 'Offline wins',
  'offline.column.outcomes': 'Paid · held · lost',
  'offline.column.last': 'Latest',
  'offline.outcomes': '{earned} · {withheld} · {lost}',
  'offline.overAllowance': 'Over the allowance',
  'offline.deactivated': 'Deactivated',
  'offline.showEvents': 'Events',
  'offline.events.title': 'Latest offline claims',
  'offline.events.description':
    'Each claim with what the server decided. Held claims are ruled on from the review tab.',
  'offline.events.focused': 'Showing one account:',
  'offline.events.showAll': 'Show everyone',
  'offline.events.emptyTitle': 'No offline claims',
  'offline.decision.earned': 'Paid',
  'offline.decision.withheld': 'Held for review',
  'offline.decision.missed': 'Lost — too slow or too late',
  'offline.decision.refused': 'Refused — ring already settled',

  // ── Rewards ───────────────────────────────────────────────────────────────
  'economy.title': 'Rewards',
  'economy.description': "How missed mornings become other people's credits",
  'economy.tabsLabel': 'Reward sections',
  'economy.tab.runs': 'Settlements',
  'economy.tab.schemes': 'Schemes',

  'economy.runs.draftTitle': 'Settle a night',
  'economy.runs.draftDescription':
    'Computes every share and moves nothing. You commit it after looking at it.',
  'economy.runs.draftAction': 'Draft yesterday',
  'economy.runs.drafted':
    'Drafted: {winners} of {participants} woke up, pool {pool}',
  'economy.runs.emptyTitle': 'No settlements yet',
  'economy.runs.emptyDescription':
    'Draft one to see who would be paid what, before any credit moves.',
  'economy.runs.confirmTitle': 'Draft a settlement for yesterday?',
  'economy.runs.confirmDescription':
    'Nothing moves. Every share is computed and written as a draft you can look at, commit or throw away.',
  'economy.runs.confirmAction': 'Draft it',
  'economy.column.night': 'Night',
  'economy.column.wokeUp': 'Woke up',
  'economy.column.pool': 'Pool',
  'economy.column.commission': 'Commission',
  'economy.column.paid': 'Paid out',
  'economy.column.startedBy': 'Started by',

  'economy.run.title': 'Settlement · {when}',
  'economy.run.discard': 'Discard',
  'economy.run.payOut': 'Pay it out',
  'economy.run.draftNote':
    'Nothing has moved yet. These are the shares that would be paid.',
  'economy.run.paid': 'Paid {amount}',
  'economy.run.cancelled': 'Settlement cancelled',
  'economy.run.section.night': 'The night',
  'economy.run.row.window': 'Window',
  'economy.run.row.settled': 'Rings settled',
  'economy.run.row.wokeUp': 'Woke up',
  'economy.run.row.missed': 'Missed',
  'economy.run.row.withheld': 'Withheld pending review',
  'economy.run.section.books': 'The books',
  'economy.run.row.carriedIn': 'Carried in from last time',
  'economy.run.row.pool': 'Pool',
  'economy.run.row.commission': 'Commission',
  'economy.run.row.paidToUsers': 'Paid to users',
  'economy.run.row.carriedForward': 'Carried forward',
  'economy.run.section.schemes': 'What each scheme did',
  'economy.run.noSchemes': 'No schemes were enabled.',
  'economy.run.fromPool': 'From the pool',
  'economy.run.minted': 'Minted',
  'economy.run.schemeApplied':
    '{recipients} recipients · {paid} paid from a pot of {pool}',
  'economy.run.schemeApplied_one':
    '{recipients} recipient · {paid} paid from a pot of {pool}',
  'economy.run.schemeCommission': '{amount} commission',
  'economy.run.schemeSkipped': 'Did not apply — {reason}',
  'economy.run.section.allocations': 'Who gets what',
  'economy.run.noAllocations': 'Nobody was allocated a share.',
  'economy.run.allocationLine': '{scheme} · weight {weight} · {percent}%',
  'economy.run.allocationSolved': 'solved in {duration}',
  'economy.run.allocationStreak': '{count}-day streak',
  'economy.run.commitTitle': 'Pay out {amount}?',
  'economy.run.commitDescription':
    'Each share is credited individually and marked paid as it lands. This cannot be undone from here.',
  'economy.run.cancelTitle': 'Discard this draft?',
  'economy.run.cancelDescription':
    'The draft is kept as a record that a settlement was computed and deliberately not paid.',

  'economy.schemes.title': 'How a night is shared',
  'economy.schemes.description':
    'Schemes run in priority order, and each sees what the earlier ones already spent.',
  'economy.schemes.new': 'New scheme',
  'economy.schemes.emptyTitle': 'No schemes yet',
  'economy.schemes.emptyDescription':
    'Without one, a settlement collects the forfeits and pays nobody. Start with an even split.',
  'economy.schemes.emptyAction': 'Create the first scheme',
  'economy.schemes.mintedBonus': 'Minted bonus',
  'economy.schemes.commissionPercent': '{percent}% commission',
  'economy.schemes.splitsBy': 'Splits by',
  'economy.schemes.appliesWhen': 'Applies when',

  'economy.basis.equal': 'Even split',
  'economy.basis.speed': 'How fast they solved it',
  'economy.basis.streak': 'How long their streak is',
  'economy.basis.stake': 'What they staked',
  'economy.basis.rank': 'Finishing position (tiers)',
  'economy.basis.custom': 'A formula',

  'economy.editor.newTitle': 'New reward scheme',
  'economy.editor.editTitle': 'Edit “{name}”',
  'economy.editor.create': 'Create scheme',
  'economy.editor.namePlaceholder': 'e.g. Morning share-out',
  'economy.editor.descriptionHint': 'What this scheme is for',
  'economy.editor.enabledYes': 'Yes — runs on every settlement',
  'economy.editor.enabledNo': 'No — kept but never applied',
  'economy.editor.priorityHint': 'Lower runs first and sees the full pool',
  'economy.editor.section.source': 'Where the credits come from',
  'economy.editor.source': 'Source',
  'economy.editor.sourcePoolHint':
    'What the people who missed their alarm lost. Costs the business nothing.',
  'economy.editor.sourceSystemHint':
    'Credits the business mints. This one has to have a ceiling.',
  'economy.editor.sourcePool': 'The forfeits from this night',
  'economy.editor.sourceSystem': 'A bonus the business pays',
  'economy.editor.poolShare': 'Share of the pool (%)',
  'economy.editor.poolShareHint':
    'Lower it to let several schemes split one night',
  'economy.editor.commission': 'Commission (%)',
  'economy.editor.commissionHint': "The house's cut, taken first",
  'economy.editor.perWinner': 'Credits per winner',
  'economy.editor.perWinnerHint': 'Leave at 0 to share a flat budget instead',
  'economy.editor.budget': 'Budget ceiling',
  'economy.editor.budgetHint': 'The most it may ever mint in one run',
  'economy.editor.section.split': 'How it is split',
  'economy.editor.basis': 'Basis',
  'economy.editor.weightPerWinner': 'Weight per winner',
  'economy.editor.minPayout': 'Smallest payout worth making',
  'economy.editor.minPayoutHint':
    'Shares below this are dropped rather than paid',
  'economy.editor.maxShare': 'Cap on one share (%)',
  'economy.editor.remainder': 'Leftover credits',
  'economy.editor.remainderHint':
    'Integer shares almost never divide a pot evenly — this decides where the remainder goes',
  'economy.editor.remainderLargest':
    'Hand them to the shares that lost most to rounding',
  'economy.editor.remainderHouse': 'Keep them as commission',
  'economy.editor.remainderCarry': 'Roll them into the next night',
  'economy.editor.section.applies': 'When it applies',
  'economy.editor.cycleCondition': 'Only on nights where…',
  'economy.editor.cyclePlaceholder':
    'Leave empty to apply always. e.g. missed == 0',
  'economy.editor.participantCondition': 'Only for winners where…',
  'economy.editor.participantPlaceholder':
    'Leave empty for every winner. e.g. streak >= 3',
  'economy.editor.section.eligible': 'Who is eligible',
  'economy.editor.eligibleNote':
    'Narrows it further, in the same language the user list and the notification cases use. Leave empty for everyone who woke up.',
  'economy.editor.updated': 'Scheme updated',
  'economy.editor.created': 'Scheme created',
  'economy.editor.deleted': 'Scheme deleted — it will not run again',
  'economy.editor.deleteTitle': 'Delete “{name}”?',
  'economy.editor.createTitle': 'Create this scheme?',
  'economy.editor.deleteDescription':
    'It stops applying to future settlements. The shares it already produced are kept.',
  'economy.editor.saveDescription':
    'It applies from the next settlement onwards. Existing runs are untouched.',

  'economy.formula.placeholder': 'e.g. speedScore * 3 + streak + 1',
  'economy.formula.valid': 'Valid — with the sample values this gives {result}',
  'economy.formula.variables': 'Variables and examples',

  // ── Notifications ─────────────────────────────────────────────────────────
  'notif.title': 'Notifications',
  'notif.description':
    'Send a message now, or set up the ones that send themselves',
  'notif.tabsLabel': 'Notification sections',
  'notif.tab.compose': 'Compose',
  'notif.tab.cases': 'Cases',
  'notif.tab.templates': 'Templates',
  'notif.tab.history': 'History',
  'notif.tab.blacklist': 'Silenced',
  'notif.queue.overdue': '{count} notifications past due',
  'notif.queue.overdue_one': '{count} notification past due',
  'notif.queue.scheduled': '{count} scheduled',
  'notif.queue.nextAt': 'next at {time}',

  'notif.compose.step1': 'Write the message',
  'notif.compose.step2': 'Choose who gets it',
  'notif.compose.step3': 'Test it, then send',
  'notif.compose.source': 'Where the copy comes from',
  'notif.compose.sourceAdhoc': 'Write it here — a one-off',
  'notif.compose.sourceTemplate':
    'Use a saved template — translated and reusable',
  'notif.compose.titleHint': 'Keep it short — lock screens truncate',
  'notif.compose.titlePlaceholder': 'Good morning',
  'notif.compose.bodyHint': 'Ad-hoc copy goes out as written, in one language',
  'notif.compose.bodyPlaceholder':
    'Your alarm is set for 07:00. Beat the challenge and keep your stake.',
  'notif.compose.namedUsers': 'Specific devices',
  'notif.compose.namedUsersHint':
    'Paste device ids, comma or space separated. Leave empty to use the conditions below.',
  'notif.compose.willReceive': '{count} users will receive this',
  'notif.compose.willReceive_one': '{count} user will receive this',
  'notif.compose.silencedSkipped': '(silenced users are skipped)',
  'notif.compose.testOn': 'Test on one device',
  'notif.compose.testHint': 'Delivers to that device only',
  'notif.compose.sendTest': 'Send test',
  'notif.compose.backgroundNote':
    'Sending runs in the background; progress appears under History.',
  'notif.compose.send': 'Send notification',
  'notif.compose.confirmTitle': 'Send to {count} users?',
  'notif.compose.confirmTitle_one': 'Send to {count} user?',
  'notif.compose.confirmDescription':
    'This goes out immediately and cannot be recalled.',
  'notif.compose.confirmAction': 'Send it',
  'notif.compose.sent': 'Sending to {count} users — follow it under History',
  'notif.compose.sent_one': 'Sending to {count} user — follow it under History',
  'notif.compose.testSent': 'Test notification sent',
  'notif.compose.testSkipped': 'Skipped — that user has no usable push token',
  'notif.compose.testRejected': 'The push provider rejected it',
  'notif.compose.languages': '{count} languages',
  'notif.compose.languages_one': '{count} language',
  'notif.compose.previewNote':
    'Each user receives this in their own language where a translation exists, English otherwise.',
  'notif.compose.previewPlaceholders':
    'Placeholders resolved per recipient: {list}.',

  'notif.cases.title': 'When this happens, send that',
  'notif.cases.description':
    'Conditions are re-checked at send time, not when the case fires — so a user who fixes the problem during the delay quietly drops out.',
  'notif.cases.new': 'New case',
  'notif.cases.emptyTitle': 'No cases yet',
  'notif.cases.emptyDescription':
    'A good first one: welcome a user half an hour after they register, if they still have no alarm set.',
  'notif.cases.emptyAction': 'Create the first case',
  'notif.cases.swept': 'Swept',
  'notif.cases.recurring': 'Recurring',
  'notif.cases.nextRun': 'next {time}',
  'notif.cases.lastRun': 'last ran {time}',
  'notif.cases.everyDay': 'Every day',
  'notif.cases.weekdays': 'Weekdays',
  'notif.cases.weekends': 'Weekends',
  'notif.cases.at': 'at',
  'notif.cases.section.schedule': 'Schedule',
  'notif.cases.scheduleTime': 'Time',
  'notif.cases.scheduleZone': 'Time zone',
  'notif.cases.scheduleDays': 'Days',
  'notif.cases.scheduleNote':
    'Runs once at this time on the chosen days and reaches whoever matches the conditions below at that moment. Leave the conditions empty to reach everyone.',
  'notif.cases.scheduleDaysRequired': 'Pick at least one day',
  'notif.cases.day.1': 'Mon',
  'notif.cases.day.2': 'Tue',
  'notif.cases.day.3': 'Wed',
  'notif.cases.day.4': 'Thu',
  'notif.cases.day.5': 'Fri',
  'notif.cases.day.6': 'Sat',
  'notif.cases.day.7': 'Sun',
  'notif.cases.event': 'Event',
  'notif.cases.queuedSoFar': '{count} queued so far',
  'notif.cases.wait': 'wait',
  'notif.cases.send': 'send',
  'notif.cases.deletedTemplate': 'a deleted template',
  'notif.cases.cooldown': 'at most once every {hours}h',
  'notif.cases.delayMinutes': '{count} min',
  'notif.cases.delayHours': '{count}h',
  'notif.cases.delayDays': '{count} days',
  'notif.cases.delayDays_one': '{count} day',
  'notif.cases.editTitle': 'Edit “{name}”',
  'notif.cases.newTitle': 'New case',
  'notif.cases.create': 'Create case',
  'notif.cases.namePlaceholder': 'e.g. Nudge after a missed morning',
  'notif.cases.enabledNo': 'No — stops enrolling and cancels what it queued',
  'notif.cases.section.fires': 'When it fires',
  'notif.cases.trigger': 'Trigger',
  'notif.cases.sweptWarning':
    'A swept case is evaluated periodically rather than fired by an event, so it must have conditions and should have a cooldown — otherwise a standing condition notifies the same people on every pass.',
  'notif.cases.delay': 'Wait before sending (minutes)',
  'notif.cases.delayHint': '0 sends on the next pass',
  'notif.cases.expires': 'Give up after (minutes)',
  'notif.cases.expiresHint':
    'Stops a backlog delivering stale pushes. Empty never expires.',
  'notif.cases.section.sends': 'What it sends',
  'notif.cases.fillsAlso': 'This trigger also fills:',
  'notif.cases.section.conditions': 'Only if they still look like this',
  'notif.cases.conditionsNote':
    'Checked against the live account at send time, not when the case fired. That is what makes a delay useful: someone who fixes the problem in the meantime is quietly dropped.',
  'notif.cases.previewMatch': '{count} users match these conditions right now',
  'notif.cases.previewMatch_one':
    '{count} user matches these conditions right now',
  'notif.cases.previewCaveat':
    ' — a sanity check, not a forecast: an event case only reaches users who trigger it after it exists',
  'notif.cases.section.limits': 'Limits',
  'notif.cases.cooldownLabel': 'At most once every (hours)',
  'notif.cases.cooldownHint':
    'Mainly for swept cases, whose condition can stay true',
  'notif.cases.maxPerRun': 'Cap per sweep',
  'notif.cases.maxPerRunHint':
    'So a swept case cannot reach the whole base at once',
  'notif.cases.updated': 'Case updated',
  'notif.cases.created': 'Case created',
  'notif.cases.deleted': 'Case deleted — anything it had queued is cancelled',
  'notif.cases.deleteTitle': 'Delete “{name}”?',
  'notif.cases.createTitle': 'Create this case?',
  'notif.cases.deleteDescription':
    'Everything it has already queued is cancelled too, so nothing goes out after this.',
  'notif.cases.saveDescription': 'It applies to triggers fired from now on.',

  'notif.templates.title': 'Saved copy',
  'notif.templates.description':
    'Written once, translated per language, and reused by every case that sends it.',
  'notif.templates.new': 'New template',
  'notif.templates.emptyTitle': 'No templates yet',
  'notif.templates.emptyDescription':
    'Cases need one. A one-off message does not — write that straight into Compose.',
  'notif.templates.emptyAction': 'Create the first template',
  'notif.templates.usedBy': '{count} cases',
  'notif.templates.usedBy_one': '{count} case',
  'notif.templates.editTitle': 'Edit “{name}”',
  'notif.templates.newTitle': 'New template',
  'notif.templates.create': 'Create template',
  'notif.templates.nameHint': 'What an operator picks it by',
  'notif.templates.namePlaceholder': 'e.g. Missed morning nudge',
  'notif.templates.key': 'Key',
  'notif.templates.keyHint':
    'Stable machine name. Lower-case letters, digits, dots and dashes.',
  'notif.templates.section.copy': 'Copy',
  'notif.templates.addLanguage': 'Add a language',
  'notif.templates.englishRequired':
    'English is required — it is the fallback every untranslated device receives.',
  'notif.templates.placeholderLead': 'Copy may use placeholders such as',
  'notif.templates.placeholderTail':
    'They are resolved per recipient at send time.',
  'notif.templates.updated': 'Template updated',
  'notif.templates.created': 'Template created',
  'notif.templates.deleted': 'Template deleted',
  'notif.templates.deleteTitle': 'Delete “{name}”?',
  'notif.templates.createTitle': 'Create this template?',
  'notif.templates.deleteDescription':
    'Refused while any case still sends it — repoint or disable those first.',
  'notif.templates.saveDescription':
    'Cases sending this template pick up the new copy immediately.',

  'notif.history.tabsLabel': 'History views',
  'notif.history.tab.campaigns': 'Sends',
  'notif.history.tab.jobs': 'Scheduled',
  'notif.history.campaignsTitle': 'Sends',
  'notif.history.campaignsDescription':
    'Broadcasts and direct messages, newest first',
  'notif.history.column.message': 'Message',
  'notif.history.column.delivered': 'Delivered',
  'notif.history.column.skipped': 'Skipped',
  'notif.history.column.failed': 'Failed',
  'notif.history.column.sentBy': 'Sent by',
  'notif.history.column.when': 'When',
  'notif.history.fromTemplate': 'From a template',
  'notif.history.emptyTitle': 'Nothing sent yet',
  'notif.history.emptyDescription':
    'Messages you send from Compose appear here with their delivery counts.',
  'notif.history.column.outcome': 'Outcome',
  'notif.history.column.device': 'Device',
  'notif.history.column.why': 'Why',
  'notif.history.column.due': 'Due',
  'notif.history.column.settled': 'Settled',
  'notif.history.attempts': '{count} attempts',
  'notif.history.outcome': 'Outcome',
  'notif.history.status.pending': 'Waiting',
  'notif.history.status.sent': 'Sent',
  'notif.history.status.skipped': 'Skipped',
  'notif.history.status.failed': 'Failed',
  'notif.history.status.cancelled': 'Cancelled',
  'notif.history.jobsTitle': 'Scheduled sends',
  'notif.history.jobsDescription':
    'Every job a case queued, and what the worker decided when it came due',
  'notif.history.jobsEmptyTitle': 'Nothing scheduled',
  'notif.history.jobsEmptyDescription':
    'Cases queue their sends here as their triggers fire.',

  'notif.blacklist.title': 'Silenced users',
  'notif.blacklist.description':
    'They receive nothing — not broadcasts, not case notifications, not tests.',
  'notif.blacklist.add': 'Silence a user',
  'notif.blacklist.column.device': 'Device',
  'notif.blacklist.column.user': 'User',
  'notif.blacklist.column.why': 'Why',
  'notif.blacklist.column.addedBy': 'Added by',
  'notif.blacklist.unsilence': 'Unsilence',
  'notif.blacklist.emptyTitle': 'Nobody is silenced',
  'notif.blacklist.emptyDescription':
    'Add someone here when they ask to stop receiving notifications.',
  'notif.blacklist.added': 'Silenced — they will receive no pushes at all',
  'notif.blacklist.removed': 'They can receive notifications again',
  'notif.blacklist.addTitle': 'Silence this user?',
  'notif.blacklist.addDescription':
    'They will receive no notifications from any path until they are removed from this list.',
  'notif.blacklist.addAction': 'Silence them',
  'notif.blacklist.removeTitle': 'Let this user receive notifications again?',
  'notif.blacklist.removeDescription':
    'The record that they were silenced stays in the activity log.',

  // ── Activity ──────────────────────────────────────────────────────────────
  'conversions.title': 'Mode switches',
  'conversions.description':
    'Every switch between reward mode and Premium: the quote the user was shown, what moved, and how it ended.',
  'conversions.window': 'Summary window',
  'conversions.lastDays': 'Last {count} days',
  'conversions.filter.direction': 'Direction',
  'conversions.filter.anyDirection': 'Both directions',
  'conversions.filter.status': 'Status',
  'conversions.filter.anyStatus': 'Any status',
  'conversions.direction.reward_to_premium': 'Reward → Premium',
  'conversions.direction.premium_to_reward': 'Premium → Reward',
  'conversions.status.pending': 'In progress',
  'conversions.status.completed': 'Completed',
  'conversions.status.failed': 'Failed',
  'conversions.status.expired': 'Abandoned',
  'conversions.tile.toPremium': 'Switched to Premium',
  'conversions.tile.spent': '{credits} spent',
  'conversions.tile.toReward': 'Switched to reward',
  'conversions.tile.granted': '{credits} granted',
  'conversions.tile.applied': 'Premium credit applied',
  'conversions.tile.appliedCaption': 'Unused Premium taken off prices',
  'conversions.tile.collected': 'Collected by card',
  'conversions.tile.collectedCaption': 'Card part of switches, both ways',
  'conversions.tile.forfeited': 'Unused value forfeited',
  'conversions.tile.forfeitedCaption': 'Credit above the package price',
  'conversions.tile.stalled': 'Failed or abandoned',
  'conversions.tile.pending': '{count} still in progress',
  'conversions.row.toPremium': '{credits} for {plan}',
  'conversions.row.toPremiumMixed': '{credits} + {paid} by card for {plan}',
  'conversions.row.toReward': '{package}, paid {paid} by card',
  'conversions.badge.partCard': 'Part card',
  'conversions.badge.unpaidPremium': 'Unpaid Premium — no credit',
  'conversions.lateSettlement': 'Paid after expiry',
  'conversions.openUser': 'User',
  'conversions.showDetails': 'Details',
  'conversions.hideDetails': 'Hide',
  'conversions.emptyTitle': 'No mode switches',
  'conversions.emptyDescription':
    'Nothing matches these filters. Switches appear here the moment a user starts one.',
  'conversions.detail.item': 'Bought',
  'conversions.detail.price': 'Card price',
  'conversions.detail.creditsSpent': 'Daylights spent',
  'conversions.detail.periodValue': 'Period recorded as worth',
  'conversions.detail.premiumEnds': 'Premium runs until',
  'conversions.detail.remaining': 'Premium time left',
  'conversions.detail.remainingValue': 'Worth of that time',
  'conversions.detail.applied': 'Credit applied',
  'conversions.detail.forfeited': 'Credit forfeited',
  'conversions.detail.charged': 'Charged by card',
  'conversions.detail.creditsGranted': 'Daylights granted',
  'conversions.detail.balanceBefore': 'Wallet before',
  'conversions.detail.balanceValue': '{balance} total · {available} spendable',
  'conversions.detail.conversionId': 'Switch',
  'conversions.detail.userId': 'User',
  'conversions.detail.subscription': 'Subscription',
  'conversions.detail.period': 'Period traded in',
  'conversions.detail.payment': 'Payment',
  'conversions.detail.clientIp': 'IP address',
  'conversions.detail.created': 'Started',
  'conversions.detail.finished': 'Finished',
  'conversions.detail.planCredits': 'Plan’s daylight price',
  'conversions.proof.title': 'Payment check',
  'conversions.proof.legacy':
    'Not recorded — this switch was made before payments were checked.',
  'conversions.proof.status': 'Premium paid for',
  'conversions.proof.verified': 'Verified',
  'conversions.proof.unverified': 'Not paid',
  'conversions.proof.funding': 'Paid with',
  'conversions.proof.funding.card': 'Card',
  'conversions.proof.funding.daylights': 'Daylights',
  'conversions.proof.paid': 'Kept for the period',
  'conversions.proof.recorded': 'Period recorded as worth',
  'conversions.proof.evidence': 'Proved by',
  'conversions.proof.reason': 'Why no credit',
  'conversions.proof.reason.NO_SUBSCRIPTION':
    'No plan behind it — granted by an operator, or a trial',
  'conversions.proof.reason.NO_CHARGE': 'No successful charge for this period',
  'conversions.proof.reason.PAYMENT_NOT_FOUND':
    'The charge names a payment that cannot be found',
  'conversions.proof.reason.PAYMENT_NOT_CAPTURED': 'The payment never took money',
  'conversions.proof.reason.PAYMENT_REFUNDED': 'The payment was refunded',
  'conversions.proof.reason.NO_CONVERSION':
    'No completed switch bought this daylight period',
  'conversions.trail.title': 'Trail',
  'conversions.trail.payment': 'Card payment',
  'conversions.trail.opened': 'Switch opened',
  'conversions.trail.daylights_taken': 'Daylights taken',
  'conversions.trail.daylights_returned': 'Daylights returned',
  'conversions.trail.daylights_granted': 'Daylights credited',
  'conversions.trail.daylights_reclaimed': 'Daylights taken back',
  'conversions.trail.checkout_started': 'Card checkout started',
  'conversions.trail.card_verified': 'Card verified (3D Secure)',
  'conversions.trail.payment_captured': 'Payment captured',
  'conversions.trail.payment_failed': 'Payment failed',
  'conversions.trail.premium_opened': 'Premium opened',
  'conversions.trail.premium_ended': 'Premium ended',
  'conversions.trail.completed': 'Completed',
  'conversions.trail.failed': 'Failed',
  'conversions.trail.expired': 'Abandoned',
  'conversions.paymentStatus.pending': 'Waiting',
  'conversions.paymentStatus.authenticated': 'Card verified',
  'conversions.paymentStatus.captured': 'Captured',
  'conversions.paymentStatus.settled': 'Settled',
  'conversions.paymentStatus.failed': 'Failed',
  'conversions.paymentStatus.refunded': 'Refunded',
  'conversions.paymentStatus.partially_refunded': 'Partly refunded',

  'audit.title': 'Activity',
  'audit.description':
    'Every change an operator has made, and why they said they made it',
  'audit.filter.operator': 'Operator',
  'audit.filter.operatorPlaceholder': 'Username',
  'audit.filter.entity': 'What was touched',
  'audit.filter.user': 'Affected device',
  'audit.entity.all': 'Everything',
  'audit.entity.user': 'Accounts',
  'audit.entity.wallet': 'Wallets',
  'audit.entity.alarm': 'Alarms',
  'audit.entity.alarm_event': 'Claim rulings',
  'audit.entity.reward_scheme': 'Reward schemes',
  'audit.entity.payout_run': 'Settlements',
  'audit.entity.notification_rule': 'Notification cases',
  'audit.entity.notification_template': 'Templates',
  'audit.entity.notification_campaign': 'Sends',
  'audit.entity.blacklist': 'Silenced users',
  'audit.emptyTitle': 'Nothing recorded',
  'audit.emptyDescription':
    'Every change made through this console lands here.',
  'audit.userPrefix': 'user',
  'audit.from': 'from {ip}',
  'audit.valueNone': 'none',
  'audit.valueYes': 'yes',
  'audit.valueNo': 'no',

  // ── Audience filters ──────────────────────────────────────────────────────
  'filters.name': 'Name',
  'filters.nameHint': 'First name, then last name',
  'filters.namePlaceholder': 'Ali Yılmaz',
  'filters.search': 'Device id starts with',
  'filters.platform': 'Platform',
  'filters.subscribed': 'Subscribed',
  'filters.freeTrial': 'Free trial',
  'filters.pushReady': 'Push-ready',
  'filters.hasToken': 'Has a token',
  'filters.noToken': 'No token',
  'filters.activeAlarms': 'Active alarm',
  'filters.hasActiveAlarms': 'Has an active alarm',
  'filters.noActiveAlarms': 'No active alarm',
  'filters.fewer': 'Fewer conditions',
  'filters.more': 'More conditions',
  'filters.clearAll': 'Clear all',
  'filters.group.where': 'Where they are',
  'filters.countries': 'Countries',
  'filters.countriesHint': 'Two-letter codes, comma separated',
  'filters.languages': 'Languages',
  'filters.languagesHint': 'Comma separated',
  'filters.accountActive': 'Account active',
  'filters.attestation': 'Attestation',
  'filters.attestation.verified': 'Verified',
  'filters.attestation.unverified': 'Could not be checked',
  'filters.attestation.failed': 'Failed',
  'filters.attestation.unknown': 'Never checked',
  'filters.appVersion': 'App version',
  'filters.appVersionHint': '1.4 also matches 1.4.2 and its builds',
  'filters.group.hold': 'What they hold',
  'filters.balanceMin': 'Balance at least',
  'filters.balanceMax': 'Balance at most',
  'filters.spendableMin': 'Spendable at least',
  'filters.spendableMinHint': 'Balance minus what is staked',
  'filters.spendableMax': 'Spendable at most',
  'filters.earnedMin': 'Lifetime winnings at least',
  'filters.earnedMax': 'Lifetime winnings at most',
  'filters.group.use': 'How they use it',
  'filters.alarmsMin': 'Alarms at least',
  'filters.alarmsMax': 'Alarms at most',
  'filters.enabledAlarmsMin': 'Enabled alarms at least',
  'filters.enabledAlarmsMax': 'Enabled alarms at most',
  'filters.wonMin': 'Challenges won at least',
  'filters.missedMin': 'Challenges missed at least',
  'filters.wakeRateMin': 'Wake rate at least (%)',
  'filters.wakeRateMinHint': 'Users with nothing settled match no bound',
  'filters.wakeRateMax': 'Wake rate at most (%)',
  'filters.group.when': 'When they were last around',
  'filters.inactiveMin': 'Quiet for at least (days)',
  'filters.inactiveMinHint': 'Stays true tomorrow, unlike a fixed date',
  'filters.inactiveMax': 'Quiet for at most (days)',
  'filters.registeredAfter': 'Registered after',
  'filters.registeredBefore': 'Registered before',
  'filters.summary.everyone': 'Everyone',
  'filters.summary.subscribed': 'subscribed',
  'filters.summary.notSubscribed': 'not subscribed',
  'filters.summary.onFreeTrial': 'on a free trial',
  'filters.summary.notOnFreeTrial': 'not on a free trial',
  'filters.summary.deactivated': 'deactivated',
  'filters.summary.noPushToken': 'no push token',
  'filters.summary.hasActiveAlarms': 'has an active alarm',
  'filters.summary.noActiveAlarms': 'no active alarm',
  'filters.summary.quiet': 'quiet {days}d+',
  'filters.summary.spendableMax': 'spendable ≤ {value}',
  'filters.summary.spendableMin': 'spendable ≥ {value}',
  'filters.summary.wakeRateMax': 'wake rate ≤ {value}%',
  'filters.summary.wakeRateMin': 'wake rate ≥ {value}%',
  'filters.summary.liveAlarmsMax': '≤ {value} live alarms',
  'filters.summary.missedMin': '{value}+ missed',
  'filters.summary.namedUsers': '{count} named users',
  'filters.summary.namedUsers_one': '{count} named user',
  'filters.summary.namedDevices': '{count} named devices',
  'filters.summary.namedDevices_one': '{count} named device',
  'filters.summary.idContains': 'id starts with "{value}"',
  'filters.summary.name': 'named "{value}…"',
  'filters.summary.appVersion': 'on v{value}',

  // ── Schedules the API sends as English ────────────────────────────────────
  'schedule.Once': 'Once',
  'schedule.Every day': 'Every day',
  'schedule.Mon': 'Mon',
  'schedule.Tue': 'Tue',
  'schedule.Wed': 'Wed',
  'schedule.Thu': 'Thu',
  'schedule.Fri': 'Fri',
  'schedule.Sat': 'Sat',
  'schedule.Sun': 'Sun',

  // ── API enum values, rendered through `humanize` ───────────────────────────
  'enum.rang': 'Alarm rang',
  'enum.dismissed': 'Dismissed',
  'enum.reward_earned': 'Challenge won',
  'enum.reward_missed': 'Challenge missed',
  'enum.reward_withheld': 'Claim withheld',
  'enum.ring_timed_out': 'Rang out unsolved',
  'enum.created': 'Alarm created',
  'enum.deleted': 'Alarm deleted',
  'enum.rejected': 'Save refused',
  'enum.integrity_failed': 'Attestation failed',

  'enum.reward': 'Reward',
  'enum.infinite': 'Infinite',
  'enum.none': 'None',
  'enum.math': 'Maths',
  'enum.scanning': 'Scanning',
  'enum.easy': 'Easy',
  'enum.medium': 'Medium',
  'enum.hard': 'Hard',

  'enum.verified': 'Verified',
  'enum.unverified': 'Could not be checked',
  'enum.failed': 'Failed',
  'enum.unknown': 'Never checked',

  'enum.topped_up': 'Topped up',
  'enum.hold_placed': 'Stake placed',
  'enum.hold_released': 'Stake freed',
  'enum.forfeited': 'Forfeited',
  'enum.reward_payout': 'Reward payout',
  'enum.spent': 'Spent',
  'enum.adjusted': 'Adjusted by hand',

  'enum.alarm_scheduled': 'Alarm scheduled',
  'enum.alarm_schedule_reduced': 'Alarm schedule reduced',
  'enum.alarm_disabled': 'Alarm switched off',
  'enum.alarm_deleted': 'Alarm deleted',
  'enum.challenge_won': 'Challenge won',
  'enum.challenge_missed': 'Challenge missed',
  'enum.purchase': 'Purchase',
  'enum.ad_reward': 'Ad reward',
  'enum.reward_share': 'Share of a settlement',
  'enum.mode_conversion': 'Mode switch',
  'enum.reversal': 'Reversal',
  'enum.admin': 'Operator',

  'enum.pending': 'Waiting',
  'enum.processing': 'Processing',
  'enum.sending': 'Sending',
  'enum.sent': 'Sent',
  'enum.completed': 'Completed',
  'enum.skipped': 'Skipped',
  'enum.cancelled': 'Cancelled',
  'enum.paid': 'Paid',

  'enum.draft': 'Draft',
  'enum.committing': 'Paying out',
  'enum.committed': 'Paid out',
  'enum.scheduled': 'Scheduled',
  'enum.manual': 'By hand',

  'enum.user': 'Account',
  'enum.alarm': 'Alarm',
  'enum.wallet': 'Wallet',
  'enum.alarm_event': 'Claim ruling',
  'enum.reward_scheme': 'Reward scheme',
  'enum.payout_run': 'Settlement',
  'enum.notification_template': 'Template',
  'enum.notification_rule': 'Notification case',
  'enum.notification_campaign': 'Send',
  'enum.blacklist': 'Silenced user',
  'enum.system': 'System',

  'enum.user_updated': 'Account updated',
  'enum.user_activated': 'Account reactivated',
  'enum.user_deactivated': 'Account deactivated',
  'enum.user_deleted': 'Account deleted',
  'enum.user_restored': 'Account restored',
  'enum.subscription_granted': 'Subscription granted',
  'enum.subscription_cancelled': 'Subscription cancelled',
  'enum.balance_credited': 'Credits added',
  'enum.balance_debited': 'Credits taken',
  'enum.balance_forfeited': 'Stake forfeited',
  'enum.alarm_updated': 'Alarm updated',
  'enum.alarm_enabled': 'Alarm switched on',
  'enum.alarm_restored': 'Alarm restored',
  'enum.claim_approved': 'Claim approved',
  'enum.claim_rejected': 'Claim rejected',
  'enum.scheme_created': 'Scheme created',
  'enum.scheme_updated': 'Scheme updated',
  'enum.scheme_deleted': 'Scheme deleted',
  'enum.payout_run_started': 'Settlement drafted',
  'enum.payout_run_committed': 'Settlement paid out',
  'enum.payout_run_cancelled': 'Settlement discarded',
  'enum.template_created': 'Template created',
  'enum.template_updated': 'Template updated',
  'enum.template_deleted': 'Template deleted',
  'enum.notification_rule_created': 'Case created',
  'enum.notification_rule_updated': 'Case updated',
  'enum.notification_rule_deleted': 'Case deleted',
  'enum.notification_sent': 'Notification sent',
  'enum.notification_test_sent': 'Test notification sent',
  'enum.blacklist_added': 'User silenced',
  'enum.blacklist_removed': 'User unsilenced',

  // ── Withdrawals ───────────────────────────────────────────────────────────
  'nav.withdrawals': 'Withdrawals',
  'withdrawals.title': 'Withdrawals',
  'withdrawals.description':
    'Earnings users have asked to be paid. Every transfer is made by hand — approve it, send it, then record the reference.',
  'withdrawals.filter.status': 'Show',
  'withdrawals.filter.openOnly': 'Still to handle',
  'withdrawals.status.pending': 'Pending',
  'withdrawals.status.approved': 'Approved',
  'withdrawals.status.paid': 'Paid',
  'withdrawals.status.rejected': 'Rejected',
  'withdrawals.status.cancelled': 'Cancelled',
  'withdrawals.tile.pending': 'Waiting for a check',
  'withdrawals.tile.pendingCaption': 'Nobody has looked at these yet',
  'withdrawals.tile.approved': 'Waiting for a transfer',
  'withdrawals.tile.approvedCaption': 'Checked, not yet sent',
  'withdrawals.tile.open': 'Held for users',
  'withdrawals.tile.openCaption': 'Already out of their balance',
  'withdrawals.tile.paid': 'Paid out',
  'withdrawals.tile.paidCaption': 'All time',
  'withdrawals.emptyTitle': 'Nothing to handle',
  'withdrawals.emptyDescription': 'Requests appear here as soon as a user asks to be paid.',
  'withdrawals.userId': 'User',
  'withdrawals.openUser': 'Open the account',
  'withdrawals.copyIban': 'Copy the IBAN',
  'withdrawals.ibanCopied': 'IBAN copied',
  'withdrawals.ibanCopyFailed': 'Could not copy the IBAN',
  'withdrawals.reviewedBy': 'Reviewed by',
  'withdrawals.reference': 'Transfer reference',
  'withdrawals.referenceHint': "The bank's reference, so this can be reconciled later.",
  'withdrawals.referencePlaceholder': 'e.g. FT26091200123',
  'withdrawals.referenceRequired': 'Enter the transfer reference first',
  'withdrawals.reasonRequired': 'Say why it was refused — the user sees this',
  'withdrawals.rejectionReason': 'Reason given',
  'withdrawals.note': 'Internal note',
  'withdrawals.action.approve': 'Approve',
  'withdrawals.action.reject': 'Reject',
  'withdrawals.action.pay': 'Mark as paid',
  'withdrawals.confirm.approve.title': 'Approve this withdrawal?',
  'withdrawals.confirm.approve.description':
    'Confirms {amount} is good to send to {iban}. No money moves yet — make the transfer, then mark it paid.',
  'withdrawals.confirm.reject.title': 'Reject this withdrawal?',
  'withdrawals.confirm.reject.description':
    'The {amount} goes back to the user. They are shown the reason you give.',
  'withdrawals.confirm.pay.title': 'Record this transfer?',
  'withdrawals.confirm.pay.description':
    'Only after {amount} has actually been sent to {iban}.',
  'withdrawals.toast.approve': 'Approved. Make the transfer, then mark it paid.',
  'withdrawals.toast.reject': 'Rejected — the earnings went back to the user',
  'withdrawals.toast.pay': 'Recorded as paid',

  // ── Referrals ─────────────────────────────────────────────────────────────
  'nav.referrals': 'Referrals',
  'referrals.title': 'Referrals',
  'referrals.description':
    'Invite-a-friend campaigns, who joined with which code, and what was paid',
  'referrals.newCampaign': 'New campaign',
  'referrals.tabsLabel': 'Referral sections',
  'referrals.tab.campaigns': 'Campaigns',
  'referrals.tab.redemptions': 'Who joined',
  'referrals.tab.rewards': 'Rewards',
  'referrals.tab.activity': 'Log',

  'referrals.tile.live': 'Live campaigns',
  'referrals.tile.liveCaption': '{scheduled} scheduled · {ended} ended',
  'referrals.tile.joined': 'Friends joined',
  'referrals.tile.joinedCaption': '{pending} waiting for a first top-up',
  'referrals.tile.paid': 'Rewards paid',
  'referrals.tile.paidCaption': '{daylights} · {days} Premium days',
  'referrals.tile.failed': 'Failed rewards',
  'referrals.tile.failedCaption': 'Retry them from the Rewards tab',

  'referrals.policy.window': 'Codes can be entered for {hours} h after registering',
  'referrals.policy.closed': 'Code entry is switched off',
  'referrals.policy.signIn': 'Google/Apple sign-in required',
  'referrals.policy.noSignIn': 'Sign-in not required',
  'referrals.policy.ip': 'at most {count} per address per {hours} h',
  'referrals.policy.noIp': 'no per-address limit',

  'referrals.status.live': 'Live',
  'referrals.status.scheduled': 'Scheduled',
  'referrals.status.ended': 'Ended',
  'referrals.audience.all': 'Everyone',
  'referrals.audience.users': 'Named accounts',
  'referrals.audienceHint.all':
    'Every account gets a code the first time it opens its codes screen.',
  'referrals.audienceHint.users':
    'Only the accounts you name get a code. You can add more later.',
  'referrals.qualification.signup': 'When the friend enters the code',
  'referrals.qualification.first_top_up': 'After the friend’s first top-up',
  'referrals.qualificationHint.signup':
    'Costs a fake account nothing — keep the reward small, or prefer the first top-up.',
  'referrals.qualificationHint.first_top_up':
    'The friend counts once they buy daylights with real money. The safe choice.',
  'referrals.rewardType.daylights': 'Daylights',
  'referrals.rewardType.free_trial': 'Free Premium days',
  'referrals.reward.trialDays': '{count} days of Premium',
  'referrals.reward.trialDays_one': '{count} day of Premium',

  'referrals.offer.signup': 'Bring {count} friends who join',
  'referrals.offer.signup_one': 'Bring {count} friend who joins',
  'referrals.offer.first_top_up': 'Bring {count} friends who make a first top-up',
  'referrals.offer.first_top_up_one': 'Bring {count} friend who makes a first top-up',
  'referrals.offer.limited': 'up to {count} times per account',
  'referrals.offer.limited_one': 'once per account',
  'referrals.offer.unlimited': 'every time, no limit',

  'referrals.field.audience': 'Who gets codes',
  'referrals.field.qualification': 'A friend counts',
  'referrals.field.requiredReferrals': 'Friends needed',
  'referrals.field.rewardType': 'Reward',
  'referrals.field.reward': 'Reward',
  'referrals.field.daylights': 'Daylights paid',
  'referrals.field.trialDays': 'Premium days given',
  'referrals.field.maxRewards': 'Times one account can earn it',
  'referrals.field.startsAt': 'Starts',
  'referrals.field.endsAt': 'Expires',
  'referrals.field.codes': 'Codes',
  'referrals.field.createdBy': 'Created by',
  'referrals.immediately': 'Immediately',
  'referrals.neverExpires': 'Never expires',
  'referrals.archived': 'Archived campaign',
  'referrals.openAccount': 'Open the account',

  'referrals.filter.status': 'Status',
  'referrals.filter.anyStatus': 'Any status',
  'referrals.filter.audience': 'Audience',
  'referrals.filter.anyAudience': 'Any audience',
  'referrals.filter.search': 'Name',
  'referrals.filter.campaign': 'Campaign',
  'referrals.filter.anyCampaign': 'Every campaign',
  'referrals.filter.code': 'Code',
  'referrals.filter.referrer': 'Referrer',
  'referrals.filter.friend': 'Friend',
  'referrals.filter.account': 'Account',
  'referrals.filter.type': 'What happened',
  'referrals.filter.anyType': 'Anything',

  'referrals.campaigns.emptyTitle': 'No campaigns yet',
  'referrals.campaigns.emptyDescription':
    'A campaign is an offer: bring N friends, get a reward. Start one for everyone, or for a few accounts.',
  'referrals.campaigns.failedBadge': '{count} failed',
  'referrals.campaigns.endsOn': 'Expires {date}',
  'referrals.campaigns.endedOn': 'Ended {date}',
  'referrals.campaigns.joinedLine': '{joined} joined · {qualified} qualified',
  'referrals.campaigns.rewardsLine': '{count} rewards paid',
  'referrals.campaigns.rewardsLine_one': '{count} reward paid',

  'referrals.editor.newTitle': 'New campaign',
  'referrals.editor.editTitle': 'Edit {name}',
  'referrals.editor.create': 'Create campaign',
  'referrals.editor.created': 'Campaign created',
  'referrals.editor.updated': 'Campaign saved',
  'referrals.editor.confirmCreate': 'Create this campaign?',
  'referrals.editor.confirmUpdate': 'Save these changes?',
  'referrals.editor.nameHint': 'Users see this next to their code.',
  'referrals.editor.namePlaceholder': 'Spring invite',
  'referrals.editor.descriptionHint':
    'Shown to users under the name. Say what they get and what their friend has to do.',
  'referrals.editor.descriptionPlaceholder':
    'Invite 3 friends. When each buys their first daylights, you get 100 daylights.',
  'referrals.editor.section.audience': 'Who',
  'referrals.editor.section.terms': 'The offer',
  'referrals.editor.section.window': 'When',
  'referrals.editor.audienceFixed': 'Fixed once the campaign exists.',
  'referrals.editor.members': 'Device ids',
  'referrals.editor.membersHint':
    '{count} of at most {max} — one per line, or comma-separated.',
  'referrals.editor.membersPlaceholder': 'Paste device ids, one per line',
  'referrals.editor.membersLater': 'Add or switch off accounts from the campaign page.',
  'referrals.editor.termsLocked': 'Terms locked',
  'referrals.editor.termsLockedHint':
    'Somebody has already used a code, so the offer is fixed — it is what referrers were promised. Expire this campaign and start a new one to change it.',
  'referrals.editor.requiredHint': 'Qualified friends per reward.',
  'referrals.editor.daylightsHint': 'Credited to the referrer’s wallet.',
  'referrals.editor.trialHint':
    'At most 365. Adds to a running trial. Accounts already paying for Premium gain nothing — give them daylights.',
  'referrals.editor.maxRewardsHint': 'Once is the safe default.',
  'referrals.editor.unlimited': 'No limit — pay every time',
  'referrals.editor.startsHint': 'Empty: starts now.',
  'referrals.editor.endsHint': 'Empty: never expires.',
  'referrals.editor.error.name': 'Give the campaign a name.',
  'referrals.editor.error.required': 'Friends needed must be 1–{max}.',
  'referrals.editor.error.daylights': 'Daylights must be 1–{max}.',
  'referrals.editor.error.trialDays': 'Premium days must be 1–{max}.',
  'referrals.editor.error.maxRewards': 'The limit must be 1–{max}, or no limit.',
  'referrals.editor.error.window': 'A campaign cannot expire before it starts.',
  'referrals.editor.error.endInPast': 'The expiry has to be in the future.',
  'referrals.editor.error.badIds': 'Not device ids: {ids}',
  'referrals.editor.error.noMembers': 'Paste at least one device id.',
  'referrals.editor.error.tooManyMembers': 'At most {max} accounts at a time.',

  'referrals.drawer.loading': 'Campaign',
  'referrals.drawer.edit': 'Edit',
  'referrals.drawer.end': 'Expire now',
  'referrals.drawer.archive': 'Archive',
  'referrals.drawer.showJoined': 'Who joined',
  'referrals.drawer.showRewards': 'Rewards',
  'referrals.drawer.showActivity': 'Log',
  'referrals.drawer.codesActive': '{active} working of {total}',
  'referrals.drawer.members': 'Accounts in this campaign',
  'referrals.drawer.holders': 'Accounts holding a code',
  'referrals.drawer.holdersHint':
    'A code is made the first time an account opens its codes screen, so only those accounts are listed.',
  'referrals.drawer.addMembers': 'Add accounts',
  'referrals.drawer.noCodes': 'Nobody holds a code yet',
  'referrals.drawer.membersAdded':
    '{added} added · {reactivated} switched back on · {already} already in',
  'referrals.drawer.done.end': 'Campaign expired — its codes no longer work',
  'referrals.drawer.done.archive': 'Campaign archived',
  'referrals.drawer.confirm.end.title': 'Expire this campaign now?',
  'referrals.drawer.confirm.end.description':
    'Its codes stop working immediately. Friends who already joined still count and still pay their referrer. Re-open it any time by editing the expiry.',
  'referrals.drawer.confirm.end.action': 'Expire now',
  'referrals.drawer.confirm.archive.title': 'Archive this campaign?',
  'referrals.drawer.confirm.archive.description':
    'It disappears from the app, its codes stop working and nothing more is paid — not even for friends still waiting on their first top-up. Its history is kept.',
  'referrals.drawer.confirm.archive.action': 'Archive',
  'referrals.drawer.confirm.members.title': 'Add accounts to this campaign',
  'referrals.drawer.confirm.members.description':
    'Each gets its own code. An account whose code was switched off gets it back.',
  'referrals.drawer.confirm.members.action': 'Add accounts',

  'referrals.stat.joined': 'Joined',
  'referrals.stat.qualified': 'Qualified',
  'referrals.stat.pending': 'Awaiting top-up',
  'referrals.stat.rewards': 'Rewards paid',
  'referrals.stat.paid': 'Paid out',
  'referrals.stat.failed': 'Failed rewards',

  'referrals.code.off': 'Off',
  'referrals.code.progress': '{qualified} qualified · {pending} waiting',
  'referrals.code.disable': 'Switch off',
  'referrals.code.enable': 'Switch on',
  'referrals.code.disabled': 'Code switched off',
  'referrals.code.enabled': 'Code switched on',
  'referrals.code.confirmDisable': 'Switch off {code}?',
  'referrals.code.confirmEnable': 'Switch {code} back on?',
  'referrals.code.confirmDisableDescription':
    'It can no longer be entered and earns nothing more. For a campaign of named accounts, this takes the account out.',
  'referrals.code.confirmEnableDescription':
    'It works again, and anything it earned while off is paid now.',

  'referrals.referralStatus.pending': 'Awaiting top-up',
  'referrals.referralStatus.qualified': 'Qualified',
  'referrals.referralStatus.revoked': 'Revoked',
  'referrals.redemptions.friend': 'Friend',
  'referrals.redemptions.code': 'Code',
  'referrals.redemptions.campaign': 'Campaign',
  'referrals.redemptions.referrer': 'Referrer',
  'referrals.redemptions.status': 'Status',
  'referrals.redemptions.redeemedAt': 'Joined',
  'referrals.redemptions.qualifiedAt': 'Qualified',
  'referrals.redemptions.ip': 'Address',
  'referrals.redemptions.sameIp': 'Same IP as referrer',
  'referrals.redemptions.revoke': 'Revoke',
  'referrals.redemptions.revoked': 'Referral revoked',
  'referrals.redemptions.confirmTitle': 'Revoke this referral?',
  'referrals.redemptions.confirmDescription':
    'The friend who used {code} stops counting towards the referrer’s reward. A reward already paid is not taken back — adjust the wallet for that.',
  'referrals.redemptions.emptyTitle': 'Nobody has joined with a code yet',
  'referrals.redemptions.emptyDescription':
    'Every friend who enters a code shows up here, with both accounts and the address it came from.',

  'referrals.rewardStatus.pending': 'Being paid',
  'referrals.rewardStatus.granted': 'Paid',
  'referrals.rewardStatus.failed': 'Failed',
  'referrals.rewards.referrer': 'Referrer',
  'referrals.rewards.reward': 'Reward',
  'referrals.rewards.campaign': 'Campaign',
  'referrals.rewards.ordinal': 'No.',
  'referrals.rewards.status': 'Status',
  'referrals.rewards.when': 'When',
  'referrals.rewards.retry': 'Retry',
  'referrals.rewards.retried': 'Reward paid',
  'referrals.rewards.retryFailed': 'Still failing: {reason}',
  'referrals.rewards.confirmTitle': 'Try this reward again?',
  'referrals.rewards.confirmDescription':
    'Pays {reward} to the referrer. A reward can only ever be paid once.',
  'referrals.rewards.emptyTitle': 'No rewards yet',
  'referrals.rewards.emptyDescription':
    'A reward appears once enough of a referrer’s friends qualify.',

  'referrals.event.redeemed': 'Code used',
  'referrals.event.denied': 'Code refused',
  'referrals.event.qualified': 'Friend qualified',
  'referrals.event.revoked': 'Referral revoked',
  'referrals.event.reward_granted': 'Reward paid',
  'referrals.event.reward_failed': 'Reward failed',
  'referrals.denial.disabled': 'Code entry is switched off.',
  'referrals.denial.invalid_code': 'No such code, or it is switched off.',
  'referrals.denial.campaign_not_live': 'The campaign had not started, or had ended.',
  'referrals.denial.window_closed': 'The account was older than the entry window.',
  'referrals.denial.already_redeemed': 'The account had already used a code.',
  'referrals.denial.device_already_redeemed':
    'This phone had already used a code, under another account.',
  'referrals.denial.self_referral': 'Its own code.',
  'referrals.denial.referrer_unavailable': 'The code’s owner is deactivated or deleted.',
  'referrals.denial.account_inactive': 'The account is deactivated.',
  'referrals.denial.integrity_failed':
    'Attestation said this device or app is not genuine.',
  'referrals.denial.ip_limit_reached': 'Too many codes from this address in the window.',
  'referrals.activity.daylights': '{count} daylights',
  'referrals.activity.daylights_one': '{count} daylight',
  'referrals.activity.by': 'by {actor}',
  'referrals.activity.emptyTitle': 'Nothing has happened yet',
  'referrals.activity.emptyDescription':
    'Every code used or refused, every friend who qualifies and every reward lands here.',

  'audit.entity.referral_campaign': 'Referral campaigns',
  'audit.entity.referral_code': 'Referral codes',
  'audit.entity.referral': 'Referrals',
  'audit.entity.referral_reward': 'Referral rewards',

  'enum.referral': 'Referral',
  'enum.referral_campaign': 'Referral campaign',
  'enum.referral_code': 'Referral code',
  'enum.referral_reward': 'Referral reward',
  'enum.referral_campaign_created': 'Referral campaign created',
  'enum.referral_campaign_updated': 'Referral campaign updated',
  'enum.referral_campaign_ended': 'Referral campaign expired',
  'enum.referral_campaign_deleted': 'Referral campaign archived',
  'enum.referral_members_added': 'Accounts added to a campaign',
  'enum.referral_code_disabled': 'Referral code switched off',
  'enum.referral_code_enabled': 'Referral code switched on',
  'enum.referral_revoked': 'Referral revoked',
  'enum.referral_reward_retried': 'Referral reward retried',

  // ── Backups ───────────────────────────────────────────────────────────────
  'nav.backups': 'Backups',
  'backups.title': 'Backups',
  'backups.description':
    'The whole database, compressed and copied to Google Drive on a schedule.',
  'backups.action.backupNow': 'Back up now',
  'backups.action.running': 'Backing up…',
  'backups.toast.started': 'Backup started — it runs in the background',
  'backups.toast.disconnected': 'Google Drive disconnected',
  'backups.tile.last': 'Last backup',
  'backups.tile.lastNone': 'No successful backup yet',
  'backups.tile.next': 'Next backup',
  'backups.tile.nextNone': 'Not scheduled',
  'backups.tile.nextRunning': 'Running now',
  'backups.tile.paused': 'Schedule paused',
  'backups.tile.stored': 'In Drive',
  'backups.tile.failures': 'Failures',
  'backups.tile.failuresCaption': 'Last 7 days',
  'backups.running.title': 'Backing up the database',
  'backups.running.detail': '{file} · started {since}',
  'backups.drive.title': 'Google Drive',
  'backups.drive.description': 'Where the backups are stored',
  'backups.drive.notConfigured': 'Not configured on the server',
  'backups.drive.notConfiguredDetail':
    'The server needs the Google OAuth client and the encryption key before a Drive can be connected:',
  'backups.drive.connected': 'Connected',
  'backups.drive.connectedDetail': 'Backups are written to this account’s Drive.',
  'backups.drive.account': 'Google account',
  'backups.drive.folder': 'Folder',
  'backups.drive.connectedAt': 'Connected',
  'backups.drive.openFolder': 'Open in Drive',
  'backups.drive.switch': 'Use another account',
  'backups.drive.disconnect': 'Disconnect',
  'backups.drive.disconnectTitle': 'Disconnect Google Drive?',
  'backups.drive.disconnectDetail':
    'Backups stop until a Drive is connected again. Files already in Drive are kept; reconnecting the same account carries on in the same folder.',
  'backups.drive.revoked': 'Google withdrew access',
  'backups.drive.revokedDetail':
    'Access for {account} was removed, so nothing is being backed up. Reconnect — the same account keeps its folder.',
  'backups.drive.none': 'No Drive connected',
  'backups.drive.noneDetail':
    'Connect a Google account and the database is backed up to its Drive on the schedule below.',
  'backups.drive.pointScope':
    'The app can only see files it created — the rest of the Drive stays private.',
  'backups.drive.pointFolder': 'A dedicated backups folder is created in the Drive.',
  'backups.drive.pointToken':
    'The access key is stored encrypted, and disconnecting revokes it at Google.',
  'backups.drive.connect': 'Connect Google Drive',
  'backups.drive.reconnect': 'Reconnect',
  'backups.drive.forget': 'Forget this account',
  'backups.schedule.title': 'Schedule',
  'backups.schedule.description': 'How often, and how many to keep',
  'backups.schedule.enabled': 'Automatic backups',
  'backups.schedule.enabledHint': '“Back up now” works either way.',
  'backups.schedule.interval': 'Frequency',
  'backups.schedule.retention': 'Backups to keep',
  'backups.schedule.retentionHint':
    'About {days} days of history. Older ones go to the Drive trash.',
  'backups.schedule.retentionRange': 'Between {min} and {max}.',
  'backups.schedule.confirmDetail':
    'The next scheduled backup is counted from the last one.',
  'backups.interval.hours': 'Every {count} hours',
  'backups.interval.hours_one': 'Every hour',
  'backups.interval.days': 'Every {count} days',
  'backups.interval.days_one': 'Every day',
  'backups.interval.weeks': 'Every {count} weeks',
  'backups.interval.weeks_one': 'Every week',
  'backups.history.title': 'History',
  'backups.history.description': 'Every backup, newest first',
  'backups.history.all': 'All',
  'backups.history.started': 'Started',
  'backups.history.status': 'Status',
  'backups.history.trigger': 'Started by',
  'backups.history.duration': 'Duration',
  'backups.history.size': 'Size',
  'backups.history.file': 'File',
  'backups.history.open': 'Open',
  'backups.history.pruned': 'In Drive trash',
  'backups.history.emptyTitle': 'No backups yet',
  'backups.history.emptyDescription':
    'Connect a Drive and press “Back up now”, or wait for the schedule.',
  'backups.status.running': 'Running',
  'backups.status.succeeded': 'Stored',
  'backups.status.failed': 'Failed',
  'backups.trigger.scheduled': 'Schedule',
  'backups.trigger.manualBy': '{actor}',
  'backups.callback.working': 'Connecting Google Drive…',
  'backups.callback.connected': 'Google Drive connected: {account}',
  'backups.callback.cancelled': 'Connection cancelled on Google’s screen',
  'backups.callback.failed': 'Google could not connect ({reason})',
  'backups.callback.missing': 'Google’s answer was incomplete — connect again',
  'audit.entity.backup': 'Backups',
  'enum.backup': 'Backup',
  'enum.backup_drive_connected': 'Backup Drive connected',
  'enum.backup_drive_disconnected': 'Backup Drive disconnected',
  'enum.backup_settings_updated': 'Backup schedule changed',
  'enum.backup_run_started': 'Backup started by hand',
};
